import { useReactiveVar } from '@apollo/client';
import {
  faBuilding,
  faCheck,
  faChevronRight,
  faMinus,
  faSearch,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { useEffect, useMemo, useRef, useState } from 'react';

import FilterDropdown from '../FilterDropdown';
import FilterPill from '../FilterPill';
import { buildMultiChipLabel } from '../helpers';

import { useGetCustomerAndAccountsQuery } from '@/graphql';
import { CustomerOnboardingStatusEnum } from '@/types/graphql';
import { currentAgencyVar } from '@/util/apollo/cache';

type CustomerOption = {
  id: string;
  name: string;
  accounts: { id: string; name: string }[];
};

type Props = {
  customerIds: string[] | undefined;
  /** flat list of selected account IDs (across all selected customers) */
  accountIds: string[] | undefined;
  onApply: (next: {
    customerIds: string[] | undefined;
    accountIds: string[] | undefined;
  }) => void;
  onClear: () => void;
};

const ClientPill = ({ customerIds, accountIds, onApply, onClear }: Props) => {
  const currentAgency = useReactiveVar(currentAgencyVar);
  const wrapRef = useRef<HTMLDivElement>(null);

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  /** Staged selections — local while the dropdown is open. */
  const [stagedCustomers, setStagedCustomers] = useState<string[]>([]);
  const [stagedAccounts, setStagedAccounts] = useState<
    Record<string, string[]>
  >({});
  const [focusedCustomerId, setFocusedCustomerId] = useState<string | null>(
    null,
  );

  const { data } = useGetCustomerAndAccountsQuery({
    variables: {
      agencyId: currentAgency!.id,
      filters: {
        status: [1],
        onboardingStatus: [CustomerOnboardingStatusEnum.COMPLETED],
      },
    },
  });

  const customers: CustomerOption[] = useMemo(() => {
    const list = data?.agency.customers.items || [];
    return list
      .map((c) => ({
        id: c.id,
        name: c.name,
        accounts: (c.accounts || []).map((a) => ({ id: a.id, name: a.name })),
      }))
      .filter((c) => c.accounts.length > 0)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [data]);

  const customersById = useMemo(() => {
    const map = new Map<string, CustomerOption>();
    customers.forEach((c) => map.set(c.id, c));
    return map;
  }, [customers]);

  /** Resync staged state from applied state whenever the dropdown opens.
   *  If a selected customer has no explicit account subset applied, default
   *  to all of that customer's accounts (matches the UI expectation that
   *  selecting a client implies "all of its billing accounts"). */
  useEffect(() => {
    if (!open) return;
    const cust = customerIds || [];
    setStagedCustomers(cust);
    const groupedAccounts: Record<string, string[]> = {};
    const accountIdSet = new Set(accountIds || []);
    cust.forEach((cid) => {
      const customer = customersById.get(cid);
      if (!customer) return;
      const matched = customer.accounts
        .filter((a) => accountIdSet.has(a.id))
        .map((a) => a.id);
      groupedAccounts[cid] =
        matched.length > 0 ? matched : customer.accounts.map((a) => a.id);
    });
    setStagedAccounts(groupedAccounts);
    setFocusedCustomerId(cust[0] || null);
    setSearch('');
  }, [open, customerIds, accountIds, customersById]);

  const filteredCustomers = useMemo(() => {
    if (!search) return customers;
    const q = search.toLowerCase();
    return customers.filter((c) => c.name.toLowerCase().includes(q));
  }, [customers, search]);

  const totalCustomers = customers.length;
  const allSelected =
    stagedCustomers.length > 0 && stagedCustomers.length === totalCustomers;
  const indeterminate =
    stagedCustomers.length > 0 && stagedCustomers.length < totalCustomers;

  const toggleCustomerAll = () => {
    if (allSelected) {
      setStagedCustomers([]);
      setStagedAccounts({});
      setFocusedCustomerId(null);
    } else {
      setStagedCustomers(customers.map((c) => c.id));
      setStagedAccounts((prev) => {
        const next = { ...prev };
        customers.forEach((c) => {
          // Default newly-checked clients to all of their billing accounts.
          if (!next[c.id] || next[c.id].length === 0) {
            next[c.id] = c.accounts.map((a) => a.id);
          }
        });
        return next;
      });
      if (!focusedCustomerId) setFocusedCustomerId(customers[0]?.id || null);
    }
  };

  const toggleCustomer = (cid: string) => {
    setStagedCustomers((prev) => {
      if (prev.includes(cid)) {
        // unchecking — drop billing entry
        setStagedAccounts((accs) => {
          const next = { ...accs };
          delete next[cid];
          return next;
        });
        return prev.filter((p) => p !== cid);
      }
      // checking — focus this client + default billing to all of its accounts.
      const customer = customersById.get(cid);
      const defaultAccounts = customer
        ? customer.accounts.map((a) => a.id)
        : [];
      setStagedAccounts((accs) =>
        accs[cid] && accs[cid].length > 0
          ? accs
          : { ...accs, [cid]: defaultAccounts },
      );
      setFocusedCustomerId(cid);
      return [...prev, cid];
    });
  };

  const focusCustomer = (cid: string) => setFocusedCustomerId(cid);

  const focusedCustomer = focusedCustomerId
    ? customersById.get(focusedCustomerId)
    : null;
  const focusedSelected = focusedCustomerId
    ? stagedCustomers.includes(focusedCustomerId)
    : false;
  const focusedAccounts = focusedCustomerId
    ? stagedAccounts[focusedCustomerId] || []
    : [];
  const focusedAllAccountsSelected =
    focusedCustomer &&
    focusedCustomer.accounts.length > 0 &&
    focusedCustomer.accounts.every((a) => focusedAccounts.includes(a.id));

  const toggleAccountAll = () => {
    if (!focusedCustomer) return;
    setStagedAccounts((prev) => ({
      ...prev,
      [focusedCustomer.id]: focusedAllAccountsSelected
        ? []
        : focusedCustomer.accounts.map((a) => a.id),
    }));
  };

  const toggleAccount = (accountId: string) => {
    if (!focusedCustomer) return;
    setStagedAccounts((prev) => {
      const current = prev[focusedCustomer.id] || [];
      return {
        ...prev,
        [focusedCustomer.id]: current.includes(accountId)
          ? current.filter((a) => a !== accountId)
          : [...current, accountId],
      };
    });
  };

  const clearAll = () => {
    setStagedCustomers([]);
    setStagedAccounts({});
    setFocusedCustomerId(null);
  };

  const apply = () => {
    const flatAccounts = stagedCustomers.flatMap(
      (cid) => stagedAccounts[cid] || [],
    );
    onApply({
      customerIds: stagedCustomers.length > 0 ? stagedCustomers : undefined,
      accountIds: flatAccounts.length > 0 ? flatAccounts : undefined,
    });
    setOpen(false);
  };

  // Chip label
  const appliedNames = (customerIds || [])
    .map((id) => customersById.get(id)?.name)
    .filter((n): n is string => Boolean(n));
  const isActive = appliedNames.length > 0;
  const { label, more } = buildMultiChipLabel(
    appliedNames.map((n) => {
      if (appliedNames.length > 1) return n; // "+N" handled by helper
      // Single customer — show billing count suffix if any
      return n;
    }),
  );

  const billingCount = (accountIds || []).length;
  const footerCount =
    stagedCustomers.length === 0
      ? ''
      : `${stagedCustomers.length} ${
          stagedCustomers.length === 1 ? 'client' : 'clients'
        }${
          Object.values(stagedAccounts).reduce((s, a) => s + a.length, 0) > 0
            ? ` · ${Object.values(stagedAccounts).reduce(
                (s, a) => s + a.length,
                0,
              )} billing ${
                Object.values(stagedAccounts).reduce(
                  (s, a) => s + a.length,
                  0,
                ) === 1
                  ? 'account'
                  : 'accounts'
              }`
            : ''
        }`;

  return (
    <div className="pill-wrap" ref={wrapRef}>
      <FilterPill
        placeholder="Client"
        chipLabel={
          isActive
            ? billingCount > 0 && appliedNames.length === 1
              ? `${label} · ${billingCount}`
              : label
            : undefined
        }
        chipMore={isActive ? more : undefined}
        open={open}
        onToggle={() => setOpen((v) => !v)}
        onClear={isActive ? onClear : undefined}
        a11yLabel="Filter by client"
      />
      <FilterDropdown
        open={open}
        onClose={() => setOpen(false)}
        anchorRef={wrapRef}
        hideApply
        className="client-panel"
      >
        <div className="client-two-col">
          {/* Client list */}
          <div className="client-col">
            <div className="dropdown-search">
              <FontAwesomeIcon icon={faSearch} color="#979797" />
              <input
                type="text"
                placeholder="Search client"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className="dropdown-scroll">
              <div
                className="client-row"
                role="checkbox"
                aria-checked={
                  allSelected ? true : indeterminate ? 'mixed' : false
                }
                onClick={toggleCustomerAll}
              >
                <span
                  className={classNames('dropdown-check', {
                    checked: allSelected,
                    indeterminate,
                  })}
                >
                  {allSelected ? (
                    <FontAwesomeIcon icon={faCheck} fixedWidth />
                  ) : indeterminate ? (
                    <FontAwesomeIcon icon={faMinus} fixedWidth />
                  ) : null}
                </span>
                <span style={{ fontSize: 14 }}>All</span>
              </div>
              {filteredCustomers.map((c) => {
                const selected = stagedCustomers.includes(c.id);
                const focused = focusedCustomerId === c.id;
                const accCount = (stagedAccounts[c.id] || []).length;
                const totalAcc = c.accounts.length;
                return (
                  <div
                    key={c.id}
                    className={classNames('client-row', {
                      selected: focused,
                    })}
                    onClick={() => focusCustomer(c.id)}
                  >
                    <span
                      role="checkbox"
                      aria-checked={selected}
                      className={classNames('dropdown-check', {
                        checked: selected,
                      })}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleCustomer(c.id);
                      }}
                    >
                      {selected ? (
                        <FontAwesomeIcon icon={faCheck} fixedWidth />
                      ) : null}
                    </span>
                    <div className="client-row-text">
                      <span className="client-row-name">{c.name}</span>
                      <span className="client-row-count">
                        {accCount}/{totalAcc} billing{' '}
                        {totalAcc === 1 ? 'account' : 'accounts'}
                      </span>
                    </div>
                    <span className="client-row-chevron">
                      <FontAwesomeIcon icon={faChevronRight} fixedWidth />
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Billing accounts pane */}
          <div className="billing-col">
            {!focusedCustomer ? (
              <div className="billing-empty">
                <FontAwesomeIcon icon={faBuilding} size="lg" color="#bdbdbd" />
                <span>Select a client first</span>
              </div>
            ) : (
              <>
                <div className="billing-title">Billing account</div>
                <div
                  className={classNames('billing-list', 'dropdown-scroll', {
                    locked: !focusedSelected,
                  })}
                >
                  <div
                    className="dropdown-option"
                    role="checkbox"
                    aria-checked={focusedAllAccountsSelected || false}
                    onClick={toggleAccountAll}
                  >
                    <span
                      className={classNames('dropdown-check', {
                        checked: focusedAllAccountsSelected,
                      })}
                    >
                      {focusedAllAccountsSelected ? (
                        <FontAwesomeIcon icon={faCheck} fixedWidth />
                      ) : null}
                    </span>
                    <span>All</span>
                  </div>
                  {focusedCustomer.accounts.map((a) => {
                    const checked = focusedAccounts.includes(a.id);
                    return (
                      <div
                        key={a.id}
                        className="dropdown-option"
                        role="checkbox"
                        aria-checked={checked}
                        onClick={() => toggleAccount(a.id)}
                      >
                        <span
                          className={classNames('dropdown-check', { checked })}
                        >
                          {checked ? (
                            <FontAwesomeIcon icon={faCheck} fixedWidth />
                          ) : null}
                        </span>
                        {a.name}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>

        <div className="client-footer">
          <span className="client-footer-count">{footerCount}</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button
              type="button"
              className={classNames('client-footer-clear', {
                active: stagedCustomers.length > 0,
              })}
              onClick={clearAll}
            >
              Clear all
            </button>
            <button
              type="button"
              className="dropdown-apply"
              style={{ width: 'auto', padding: '8px 20px' }}
              onClick={apply}
            >
              Apply selection
            </button>
          </div>
        </div>
      </FilterDropdown>
    </div>
  );
};

export default ClientPill;
